/**
 * FileAttachments — Reusable file attachment component
 * Supports up to 10 files: txt, md, csv, json, pdf (text), images
 */
import { useRef } from 'react';
import { Paperclip, X, FileText, Image, File } from 'lucide-react';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const ACCEPTED = '.txt,.md,.csv,.json,.pdf,.docx,.png,.jpg,.jpeg,.webp,.py,.js,.ts,.html,.xml';

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file, 'utf-8');
    }
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(`[Could not read ${file.name}]`);
  });
}

function fileIcon(type) {
  if (type.startsWith('image/')) return <Image size={10} />;
  if (type.includes('pdf') || type.includes('word') || type.includes('document')) return <File size={10} />;
  return <FileText size={10} />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function FileAttachments({ attachments = [], onChange, disabled = false }) {
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const fileList = Array.from(files).slice(0, MAX_FILES - attachments.length);
    const processed = [];

    for (const file of fileList) {
      if (file.size > MAX_FILE_SIZE) continue;
      const content = await readFile(file);
      processed.push({ name: file.name, type: file.type || 'text/plain', content, size: file.size });
    }

    onChange([...attachments, ...processed]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (i) => {
    onChange(attachments.filter((_, idx) => idx !== i));
  };

  const canAdd = attachments.length < MAX_FILES && !disabled;

  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {/* Chips */}
      {attachments.map((att, i) => (
        <div
          key={i}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] group"
          style={{
            background: 'rgba(124,94,250,0.1)',
            border: '1px solid rgba(124,94,250,0.3)',
            color: '#a68eff',
            maxWidth: 140,
          }}
          title={`${att.name} (${formatSize(att.size)})`}
        >
          <span className="flex-shrink-0">{fileIcon(att.type)}</span>
          <span className="truncate flex-1" style={{ maxWidth: 80 }}>{att.name}</span>
          <button
            type="button"
            onClick={() => remove(i)}
            className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            style={{ lineHeight: 0 }}
          >
            <X size={9} />
          </button>
        </div>
      ))}

      {/* Add button */}
      {canAdd && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED}
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="attach-files-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] transition-all hover:opacity-80"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--llmc-border)',
              color: 'var(--llmc-text-muted)',
            }}
          >
            <Paperclip size={10} />
            {attachments.length > 0
              ? `+file (${attachments.length}/${MAX_FILES})`
              : 'Attach'}
          </button>
        </>
      )}
    </div>
  );
}
